import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    Renderer2,
    signal,
    ViewChild,
    inject,
    effect,
    HostBinding
} from '@angular/core';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import { MatMenuTrigger } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MarkdownModule } from 'ngx-markdown';
import { MatIcon } from '@angular/material/icon';
import { find, floor, forEach, size} from 'lodash';

import {CoreModule} from "../../_core/core.module";
import { AppService } from '../../app.service';
import { OrderService } from 'src/app/_services/order.service';
import { OrderItemsUtilsService } from 'src/app/order-items/order-items-util.service';

import { ChatService, ChatMessage, Offer, OfferModifier, OfferSelection, SystemOrderRequest, OrderItem } from '../chat.service';
import { ChatBasketService } from '../chat-basket.service';

import { AudioRecorderComponent } from './audio-recorder/audio-recorder.component';
import {MatButton, MatIconButton} from "@angular/material/button";
import {OfferComponent} from "../../order-items/offer/offer.component";
import {ChatSummaryBottomSheetComponent} from "../chat-summary-bottom-sheet/chat-summary-bottom-sheet.component";



interface ProcessOffersResult {
    readonly addedItems: string[];
    readonly invalidItems: string[];
    readonly addedCount: number;
    readonly skippedCount: number;
}

// Constants for configuration
const TYPING_EFFECT_CONFIG = {
    chunkSize: 3,
    delay: 30,
    scrollThreshold: 20,
    focusDelay: 100,
    updateThreshold: 50 // Minimum time between DOM updates in ms
} as const;

@Component({
    selector: 'app-chat-dialog',
    standalone: true,
    templateUrl: './chat-dialog.component.html',
    styleUrls: ['./chat-dialog.component.scss'],
	imports: [CommonModule, FormsModule, MarkdownModule, MatIcon, AudioRecorderComponent, MatIconButton, MatButton, CoreModule],
})
export class ChatDialogComponent implements AfterViewInit, OnDestroy {
    @HostBinding('class')
    hostClasses: string = 'chat-dialog';

    // Services
    private readonly renderer = inject(Renderer2);
    private readonly dialogRef = inject(MatDialogRef<ChatDialogComponent>);
    private readonly chatService = inject(ChatService);
    private readonly chatBasketService = inject(ChatBasketService);

    private readonly orderService = inject(OrderService);
    private readonly orderUtils = inject(OrderItemsUtilsService);

    public readonly appService = inject(AppService);

    // Signals
    readonly messages = this.chatService.getConversation();
    readonly prompt = signal('');
    readonly loading = signal(false);
    readonly showResetConfirmation = signal(false);
    readonly enableSyncBasket = signal(false);
    readonly dialogLanguage = signal<string>(this.appService.locale.localeId);

	textSizes: number[] = [0.9,0.95,1,1.05,1.1];
    readonly currentTextSize = signal(this.getStoredTextSize()); // Load from localStorage or default to 4

    // Getters
    get sessionId(): string {
        return this.chatService.getSessionId();
    }

    // Component state
    private cancelController: AbortController | null = null;
    private shouldAutoScroll = true;
    private isUserInteracting = false;
    private isDestroyed = false;
    private readonly onInteractionStart = (): void => { this.isUserInteracting = true; };
    private readonly onInteractionEnd = (): void => { this.isUserInteracting = false; };
    private isScrollScheduled = false;
    private scrollAfterContentTimeout: any = null;
    private pendingBindImages = false;
    private previousSessionId = 'new';
    private hasRunInitialOfferSummary = false;
    private commandExecuted = false; // Flag to prevent multiple executions
    private processingOrder = false;

    @ViewChild('scrollContainer') private scrollContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('chatInput') private chatInput!: ElementRef<HTMLTextAreaElement>;
	@ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

	contextMenuPosition = { x: '0px', y: '0px' };


    constructor(
		public dialog: MatDialog,
		private bottomSheet: MatBottomSheet,
		) {
        this.chatService.clearSystemOrderRequest();
        // Register command handlers
        this.chatService.registerCommandHandler('make_order', () => {
            if (!this.commandExecuted) {
                this.commandExecuted = true;
                this.getChatOffersAndAddToBasket();
            }
        });

        // Watch for system order requests via signal
        effect(async () => {
            if (this.processingOrder) return;
            const orderRequest = this.chatService.systemOrderRequest();
            if (orderRequest) {
                this.processingOrder = true;
                console.log('System order request received via signal:', orderRequest);
                // Clear the signal immediately to prevent re-triggering
                this.chatService.clearSystemOrderRequest();
                // Process the order request asynchronously
                await this.addSystemOrderToBasket(orderRequest);
            }
        }, { allowSignalWrites: true });

        console.log('ChatBottomSheetComponent constructor completed, instance ID:', Date.now());

        // Use effect to watch sessionId changes - must be in injection context
        effect(() => {
            const currentSessionId = this.chatService.getSessionId();
            // Check if sessionId changed from 'new' to a valid ID OR if it's already valid on first load
            if ((this.previousSessionId === 'new' && currentSessionId !== 'new') ||
                (!this.hasRunInitialOfferSummary && currentSessionId !== 'new')) {
                console.log('Session ready for offer summary:', currentSessionId);
                this.hasRunInitialOfferSummary = true;
                // Use setInterval to run every second until loading becomes false
                const offerSummaryInterval = setInterval(async () => {
                    try {
                        // Clear interval when loading becomes false
                        if (!this.loading()) {
                            await this.getOfferSummary();
                            clearInterval(offerSummaryInterval);
                        }
                    } catch (error) {
                        console.error('Error in getOfferSummary:', error);
                        // Also clear interval on error
                        clearInterval(offerSummaryInterval);
                    }
                }, 1000);
            }
            this.previousSessionId = currentSessionId;
        });
    }

    ngAfterViewInit(): void {
        this.setDialogLanguage();
        this.initializeComponent();
        this.setupEventListeners();
        this.initializeChat();
        this.focusInput();
        // Initialize textarea height
        setTimeout(() => this.adjustTextareaHeight(), 0);

        // Initialize text size from stored value
        this.setTextSize(this.currentTextSize());

        this.focusChatInput();
    }

    ngOnDestroy(): void {
        console.log('ChatBottomSheetComponent ngOnDestroy called');

        // Mark component as destroyed to prevent further operations
        this.isDestroyed = true;

        // Clean up timeouts to prevent memory leaks and errors
        if (this.scrollAfterContentTimeout) {
            clearTimeout(this.scrollAfterContentTimeout);
            this.scrollAfterContentTimeout = null;
        }

        // Clean up drag listeners
        this.cleanupEventListeners();
        this.cancelOngoingRequest();
        console.log('ChatBottomSheetComponent ngOnDestroy completed');
    }

    focusChatInput(): void {
        if (this.appService.isDesktop()) {
            // Focus the textarea for desktop users
            setTimeout(() => {
                if (this.chatInput?.nativeElement) {
                    this.chatInput.nativeElement.focus();
                }
            }, 0);
        }
    }

    private initializeComponent(): void {
        this.scrollToBottom(true);
    }

    private setupEventListeners(): void {
        // Blur input on scroll to prevent iOS keyboard issues
        window.addEventListener('touchmove', this.blurOnScroll, { passive: true });
        window.addEventListener('wheel', this.blurOnScroll, { passive: true });

        // Listen to user scroll to disable auto scroll if user scrolled up
        this.scrollContainer.nativeElement.addEventListener('scroll', this.onUserScroll, { passive: true });

        // Mark real user interaction to avoid disabling autoscroll due to programmatic scrolls
        const el = this.scrollContainer.nativeElement;
        el.addEventListener('touchstart', this.onInteractionStart, { passive: true });
        // Close text size menu on outside click
        document.addEventListener('click', this.onDocumentClick, true);
        el.addEventListener('touchend', this.onInteractionEnd, { passive: true });
        el.addEventListener('mousedown', this.onInteractionStart);
        el.addEventListener('mouseup', this.onInteractionEnd);
        el.addEventListener('mouseleave', this.onInteractionEnd);
    }

    private cleanupEventListeners(): void {
        window.removeEventListener('touchmove', this.blurOnScroll);
        window.removeEventListener('wheel', this.blurOnScroll);
        this.scrollContainer.nativeElement?.removeEventListener('scroll', this.onUserScroll);

        const el = this.scrollContainer?.nativeElement;
        if (el) {
            el.removeEventListener('touchstart', this.onInteractionStart);
            el.removeEventListener('touchend', this.onInteractionEnd);
            el.removeEventListener('mousedown', this.onInteractionStart);
            el.removeEventListener('mouseup', this.onInteractionEnd);
            el.removeEventListener('mouseleave', this.onInteractionEnd);
        }

        document.removeEventListener('click', this.onDocumentClick, true);
    }

    private initializeChat(): void {
        this.sendMessage('', true); // Initial greeting load if applicable
    }

    private focusInput(): void {
        setTimeout(() => {
            this.chatInput?.nativeElement?.focus();
            this.adjustTextareaHeight(); // Adjust height when focusing
        }, TYPING_EFFECT_CONFIG.focusDelay);
    }

    private ensureScrollToBottom(): void {
        // Force scroll for important content like new messages
        this.shouldAutoScroll = true;
        this.scrollToBottom(false, true);
    }

    private scrollToBottom(bindImages: boolean = false, force: boolean = false): void {
        if (this.isDestroyed || (!this.shouldAutoScroll && !force)) return;

        // Remember if we need to bind images on the next scroll cycle
        if (bindImages) this.pendingBindImages = true;

        if (this.isScrollScheduled) return; // Coalesce multiple calls into one frame
        this.isScrollScheduled = true;

        requestAnimationFrame(() => {
            // Check if component is destroyed before accessing DOM
            if (this.isDestroyed) {
                this.isScrollScheduled = false;
                return;
            }

            const el = this.scrollContainer?.nativeElement;
            if (el) {
                el.scrollTop = el.scrollHeight;
            }

            // Re-run after dynamic content (e.g., images/markdown) settles
            if (this.scrollAfterContentTimeout) clearTimeout(this.scrollAfterContentTimeout);
            this.scrollAfterContentTimeout = setTimeout(() => {
                // Check again if component is destroyed before timeout execution
                if (this.isDestroyed) return;

                const element = this.scrollContainer?.nativeElement;
                if (element) {
                    element.scrollTop = element.scrollHeight;
                }

                if (this.pendingBindImages) {
                    this.bindImageClickHandlers();
                    this.pendingBindImages = false;
                }
            }, 50);

            this.isScrollScheduled = false;
        });
    }

    private readonly onUserScroll = (): void => {
        const el = this.scrollContainer.nativeElement;
        if (!el) return;

        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < TYPING_EFFECT_CONFIG.scrollThreshold;
        if (this.isUserInteracting) {
            this.shouldAutoScroll = atBottom;
        } else if (atBottom) {
            this.shouldAutoScroll = true;
        }
    };

    close(): void {
        console.log('close() method called - dismissing dialog');
        this.dialogRef.close();
    }

    private readonly blurOnScroll = (): void => {
        const activeElement = document.activeElement;
        if (activeElement === this.chatInput?.nativeElement) {
            this.chatInput.nativeElement.blur();
        }
    };

    private readonly onDocumentClick = (event: MouseEvent): void => {

    };

    /**
     * Adjusts textarea height based on content (1-4 lines)
     */
    adjustTextareaHeight(): void {
        const textarea = this.chatInput?.nativeElement;
        if (!textarea) return;

        // Reset height to calculate scrollHeight correctly
        textarea.style.height = 'auto';

        // Force reflow to ensure scrollHeight is recalculated
        textarea.offsetHeight;

        // Use the CSS values directly in pixels
        const minHeightPx = 40; // 2.5rem ≈ 40px (matches CSS min-height)
        const maxHeightPx = 96; // 6rem = 96px (matches CSS max-height)

        // Set height based on content, constrained to min/max
        const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeightPx), maxHeightPx);
        textarea.style.height = `${newHeight}px`;

        // Show/hide scrollbar when content exceeds max height
        textarea.style.overflowY = textarea.scrollHeight > maxHeightPx ? 'auto' : 'hidden';
    }

    /**
     * Handles keydown events for textarea
     */
    onTextareaKeydown(event: KeyboardEvent): void {
        // Handle Enter key behavior
        if (event.key === 'Enter') {
			// On mobile, Enter adds a new line (default behavior)
			// Only send message if Shift+Enter is pressed
			if (event.shiftKey || this.appService.isDesktop()) {
				event.preventDefault();
				this.sendMessage();
			}
			// Otherwise let default behavior happen (new line)
			return;
        }
    }

    cancelOngoingRequest(): void {
        if (this.cancelController && !this.cancelController.signal.aborted) {
            try {
                // Use a more specific abort reason to help with debugging
                this.cancelController.abort('Component destroyed');
            } catch (error) {
                // Ignore abort errors as the request may already be completed or aborted
                console.debug('AbortController already aborted:', error);
            }
        }
        this.cancelController = null;
        this.loading.set(false);
    }

    async sendMessage(input?: string, isInit = false, messageArgs?): Promise<void> {
        if (this.loading() || this.showResetConfirmation()) return;

        const message = input !== undefined ? input : this.prompt().trim();
        if (!message && !isInit) return;

        const isNewSession = this.chatService.getSessionId() === 'new';
        const noMessages = !this.hasMessages();

        if (isInit && (!isNewSession || !noMessages)) return;

        this.cancelOngoingRequest();

        if (!isInit) {
            // Convert line breaks to <p> tags for proper display
            const formattedMessage = this.chatService.formatMessageWithParagraphs(message);
            this.chatService.addMessage({
				...{
					role: 'user',
					content: formattedMessage,
					direction: this.appService.direction
				}, ...messageArgs,
			});
            this.prompt.set('');
            // Use setTimeout to ensure DOM is updated before adjusting height
            setTimeout(() => this.adjustTextareaHeight(), 0);
            // Process service images after adding user message
            this.shouldAutoScroll = true; // Re-enable auto-scroll on new user message
        }

        this.loading.set(true);
        this.scrollToBottom();

        if (this.isDestroyed) return;

        const currentMsgs: ChatMessage[] = isInit
            ? [{ role: 'assistant', content: '', direction: this.appService.direction }]
            : [...this.messages(), { role: 'assistant', content: '', direction: this.appService.direction }];

        this.chatService.replaceConversation(currentMsgs);
        const index = currentMsgs.length - 1;

        this.cancelController = new AbortController();

        const language = this.appService.selectedLocale.name;

        try {
            const organizationTime = this.chatService.getSessionId() === 'new' ? this.appService.getRealDate() : '';
            const stream = await this.chatService.sendMessageStream(
                this.dialogLanguage(),
                language,
                message,
                this.cancelController.signal,
                organizationTime
            );
            const reader = stream.getReader();
            const decoder = new TextDecoder('utf-8');
            let fullText = '';
            let lastStreamUpdate = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                fullText += chunk;

                // Show chunks as they arrive with throttling for all messages (including init)
                const now = Date.now();
                if (now - lastStreamUpdate > TYPING_EFFECT_CONFIG.updateThreshold) {
                    const updated = [...this.messages()];
                    // Filter streaming content to hide incomplete @{...}@ patterns
                    const filteredContent = this.chatService.filterStreamingContent(fullText);
                    updated[index] = {
                        role: 'assistant',
                        content: filteredContent,
                        direction: this.appService.direction,
						provisional: true
                    };
                    this.chatService.replaceConversation(updated);
                    this.scrollToBottom();
                    // Bind image handlers for images that appear during streaming
                    // Use setTimeout to ensure DOM is updated before binding handlers
                    setTimeout(() => this.bindImageClickHandlers(), 0);
                    // Process service images after binding handlers
                    lastStreamUpdate = now;
                }
            }

            // Ensure final content is displayed for all streaming messages
            if (fullText) {
                const updated = [...this.messages()];
                // Use the full text for final processing (processMessage will handle @{...}@ patterns)
                updated[index] = {
                    role: 'assistant',
                    content: fullText,
                    direction: this.appService.direction
                };
                this.chatService.replaceConversation(updated);
                // Bind images for the final content
                setTimeout(() => this.bindImageClickHandlers(), 0);
            }
        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                console.error('Chat error:', err);
                this.chatService.addMessage({
                    role: 'assistant',
                    content: this.appService.translate('tabot.chat_error'),
                    type: 'error',
                    direction: this.appService.direction
                });
            }
        } finally {
            this.loading.set(false);
            this.cancelController = null;
            this.scrollToBottom(true); // Bind images after streaming is complete
        }
    }

    /**
     * Adds system order request to basket using the new structure
     */
    private async addSystemOrderToBasket(orderRequest: SystemOrderRequest): Promise<void> {
        try {
            console.log('addSystemOrderToBasket called, processing system order request:', orderRequest);
            console.log('Component state - isDestroyed?', this.constructor.name, Date.now());

            this.scrollToBottom();
            const viewItems = this.orderService.catalogItemsAsFlatArray;

			let payload = {
				missingItems: [],
				validItems: [],
				invalidItems: [],
				errorItems: []
			}

			forEach(orderRequest.System_request_order, (orderItem) => {
				const viewItem = viewItems.find(o => o.offer === orderItem.offerId);
				if (!viewItem || viewItem._disabled) {
					payload.missingItems.push(orderItem.offerId);
					return;
				}
				try {
					const result = this.chatBasketService.processSystemOrderItem(orderItem, viewItem);
					result.quantity = orderItem.quantity;
					if (result.success) {
						payload.validItems.push(result);
					} else {
						payload.invalidItems.push(result);
					}
				} catch (error) {
					console.error(`Error processing order item "${orderItem.offerId}":`, error);
					payload.errorItems.push(viewItem);
				}
			});

			if (!orderRequest?.System_request_order?.length || !payload.validItems.length) {
				this.chatService.addMessage({
					role: 'assistant',
					content: this.appService.translate('tabot.no_offers'),
					type: 'error',
					direction: this.appService.direction
				});
				this.scrollToBottom();
				return;
			}

			let bottomSheetRef = this.bottomSheet.open(ChatSummaryBottomSheetComponent, {
				data: payload,
				direction: this.appService.direction,
				panelClass: "bottom-sheet-summary",
				disableClose: true,
			});
			bottomSheetRef.afterDismissed().subscribe(result => {
				this.removeTemporaryMessages()
				if (result) this.close();
				else this.close();
			});

        } catch (error) {
            console.error('Failed to process system order:', error);
            this.chatService.addMessage({
                role: 'assistant',
                content: this.appService.translate('tabot.general_error'),
                type: 'error',
                direction: this.appService.direction
            });
            this.scrollToBottom();
        }
    }

    /**
     * Sends System_request_order message through streamChat without displaying it in chat
     */
    async getChatOffersAndAddToBasket(): Promise<void> {
        console.log('getChatOffersAndAddToBasket called - sending System_request_order through streamChat');

        // Show creating order message
        this.chatService.addMessage({
            role: 'assistant',
            content: this.appService.translate('tabot.creating_order'),
            type: 'processing',
            direction: this.appService.direction
        });
        this.scrollToBottom();

        this.loading.set(true);
        this.cancelController = new AbortController();
        const language = this.appService.selectedLocale.name;

        try {
            // Send message directly to server without adding to chat
            const stream = await this.chatService.sendMessageStream(
                this.dialogLanguage(),
                language,
                'System_request_order',
                this.cancelController.signal
            );

            const reader = stream.getReader();
            const decoder = new TextDecoder('utf-8');
            let fullText = '';
            let lastStreamUpdate = 0;

            // Add empty assistant message for response
            const currentMsgs: ChatMessage[] = [...this.messages(), { role: 'assistant', content: '', direction: this.appService.direction }];
            this.chatService.replaceConversation(currentMsgs);
            const index = currentMsgs.length - 1;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                fullText += chunk;

                // Show chunks as they arrive with throttling
                const now = Date.now();
                if (now - lastStreamUpdate > 50) {
                    const updated = [...this.messages()];
                    const filteredContent = this.chatService.filterStreamingContent(fullText);
                    updated[index] = {
                        role: 'assistant',
                        content: filteredContent,
                        direction: this.appService.direction
                    };
                    this.chatService.replaceConversation(updated);
                    this.scrollToBottom();
                    lastStreamUpdate = now;
                }
            }

            // Final content display
            if (fullText) {
                const updated = [...this.messages()];
                updated[index] = {
                    role: 'assistant',
                    content: fullText,
                    direction: this.appService.direction
                };
                this.chatService.replaceConversation(updated);
            }
        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                console.error('Chat error:', err);
                this.chatService.addMessage({
                    role: 'assistant',
                    content: this.appService.translate('tabot.error'),
                    type: 'error',
                    direction: this.appService.direction
                });
            }
        } finally {
            this.loading.set(false);
            this.cancelController = null;
            this.scrollToBottom(true);
        }

        console.log('System_request_order message sent through streamChat without displaying in chat');
    }

    /**
     * Validates if an offer object is valid for processing
     */
    private isValidOffer(offer: any): offer is Offer {
        return offer &&
            typeof offer === 'object' &&
            offer.offer &&
            typeof offer.offer === 'string' &&
            offer.offer.trim().length > 0 &&
            offer.name &&
            typeof offer.name === 'string';
    }

    /**
     * Sends chat messages based on basket operation results
     */
    private sendBasketResultMessages(addedItems: string[], invalidItems: string[]): void {
        if (addedItems.length > 0) {
            const itemsList = addedItems.join(', ');
            const message = addedItems.length === 1
                ? this.appService.translate('tabot.single_item_needs_edit',{items:itemsList})
                : this.appService.translate('tabot.multiple_items_added',{items:itemsList});

            this.chatService.addMessage({
                role: 'assistant',
                content: message,
                direction: this.appService.direction
            });
        }

        if (invalidItems.length > 0) {
            const invalidItemsList = invalidItems.join(', ');
            const validationMessage = invalidItems.length === 1
                ? this.appService.translate('tabot.single_item_needs_edit',{items:invalidItemsList})
                : this.appService.translate('tabot.multiple_items_need_edit',{items:invalidItemsList});

            this.chatService.addMessage({
                role: 'assistant',
                content: validationMessage,
                direction: this.appService.direction
            });
        }

        if (addedItems.length > 0 || invalidItems.length > 0) {
            this.scrollToBottom();
        }
    }

    resetChat(): void {
        if (this.orderService.$storage.tabotLastBasketSummary) {
            this.orderService.$storage.tabotLastBasketSummary = '';
        }
        this.showResetConfirmation.set(true);
        this.shouldAutoScroll = true; // Force auto-scroll for new content
        this.scrollToBottom(false, true); // Force scroll to show confirmation
    }

    confirmResetChat(): void {
        this.showResetConfirmation.set(false);
        this.chatService.clearChatSession();
        this.chatService.updateSessionId('new');
        this.commandExecuted = false; // Reset command execution flag
        this.chatBasketService.clearTabotOffersFromBasket();
        this.orderService.calculateBasketTotal();
        this.sendMessage('', true);
    }

    cancelResetChat(): void {
        this.showResetConfirmation.set(false);
    }

    hasMessages(): boolean {
        return this.messages().length > 0;
    }

    /**
     * Returns filtered and validated messages for display
     */
    get validMessages(): ChatMessage[] {
        return this.messages().filter(msg =>
            msg &&
            typeof msg === 'object' &&
            typeof msg.role === 'string' &&
            msg.content !== undefined &&
            !msg.hidden
        );
    }

    /**
     * Utility method to create a delay promise
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Removes processing and error messages from the conversation based on message type
     */
    private removeProcessingAndErrorMessages(): void {
        const messages = this.messages();
        if (messages.length > 0) {
            // Filter out processing and error messages using the type field
            const filteredMessages = messages.filter(message => {
                if (message.role !== 'assistant') {
                    return true; // Keep user messages
                }

                // Remove messages with specific types
                const shouldRemove = message.type === 'processing' ||
                    message.type === 'error';

                return !shouldRemove;
            });

            // Update conversation only if messages were removed
            if (filteredMessages.length !== messages.length) {
                this.chatService.replaceConversation(filteredMessages);
                this.scrollToBottom();
            }
        }
    }

    /**
     * Removes temporary assistant messages from the conversation
     */
    private removeTemporaryMessages(): void {
        const messages = this.messages();
        if (messages.length > 0) {
            // Filter out temporary assistant messages
            const filteredMessages = messages.filter(message => message.role !== 'assistant-temporary');

            // Update conversation only if messages were removed
            if (filteredMessages.length !== messages.length) {
                this.chatService.replaceConversation(filteredMessages);
                this.scrollToBottom();
            }
        }
    }

    private bindImageClickHandlers(): void {
        if (this.isDestroyed) return;

        const container = this.scrollContainer?.nativeElement;
        if (!container) return;

        // Handle images generated from the service (with image-on-row class)
        const hElms: NodeListOf<HTMLElement> = container.querySelectorAll('.offer-row');
        hElms.forEach((hElm) => {
			this.enableSyncBasket.set(true);
            if (!hElm.getAttribute('data-click-attached')) {

				const clickHandler = (event) => {
					let viewItem = find(this.orderService.catalogItemsAsFlatArray, { _id: hElm.getAttribute('data-viewitem-id') });
					if (!viewItem) return;
					const data = this.chatService.getOfferDictionaryData(Number(hElm.getAttribute('data-index')));
					if (data) {
						try {
							const result = this.chatBasketService.processSystemOrderItem(data, viewItem);
							if (result.basketOffer) {
								viewItem = result.basketOffer;
							}
						} catch (e) {
						}
					}
					event.preventDefault();
					this.contextMenuPosition.x = event.clientX + 'px';
					this.contextMenuPosition.y = event.clientY + 'px';
					this.contextMenu.menuData = { 'item': viewItem };
					this.contextMenu.menu.focusFirstItem('mouse');
					this.contextMenu.openMenu();
				}
				this.renderer.listen(hElm, 'click', clickHandler);
				hElm.setAttribute('data-click-attached', 'true');
            }
        });
    }

	editChatOffer(viewItem) {
		this.selectOffer(viewItem);
	}
	deleteChatOffer(viewItem) {
		this.sendMessage(`without ${viewItem.name}`, false, {hidden: true});
	}

	async selectOffer(_offer) {
		if (_offer._disabled) {
			this.appService.alert({ content: this.appService.translate("ITEM_OUT_OF_STOCK_MESSAGE", { count: _offer.stock }) });
			return;
		}
		await this.orderService.validateOrderLimit(1, _offer.price);

		let dialogRef = this.dialog.open(OfferComponent, {
			panelClass: ['to-offer-dialog', 'animated', 'fast', 'slideInLeft'],
			backdropClass: 'kiosk-fullscreen-backdrop',
			direction: this.appService.direction,
			//disableClose: true,
			closeOnNavigation: true,
			data: {
				offer: _offer,
				isEdit: false,
				minWidth: 280
			},
			autoFocus: false
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.orderService.addToBasket(result.viewItem);
				this.getOfferSummary();
			}
		});

	}


    /**
     * Merges item summaries with same description by combining quantities and prices
     * @param summaries - Array of summary strings to merge
     * @param newSummary - New summary string to add or merge
     */
    private mergeItemSummary(summaries: Array<string>, newSummary: string): void {
        // Parse the summary properly - the price is always the last part after the last comma
        const lastCommaIndex = newSummary.lastIndexOf(',');
        if (lastCommaIndex === -1) {
            summaries.push(newSummary);
            return;
        }

        const contentPart = newSummary.substring(0, lastCommaIndex).trim(); // Everything before last comma
        const pricePart = newSummary.substring(lastCommaIndex + 1).trim(); // Price after last comma
        const newPrice = parseFloat(pricePart);

        if (isNaN(newPrice)) {
            summaries.push(newSummary);
            return;
        }

        // Extract quantity and item name from the first part (before first comma)
        const firstCommaIndex = contentPart.indexOf(',');
        if (firstCommaIndex === -1) {
            summaries.push(newSummary);
            return;
        }

        const newQuantityAndName = contentPart.substring(0, firstCommaIndex).trim(); // "1 סלט אצות"
        const newModifications = contentPart.substring(firstCommaIndex + 1).trim(); // All modifications

        // Extract quantity and item name
        const quantityMatch = newQuantityAndName.match(/^(\d+)\s+(.+)$/);
        if (!quantityMatch) {
            summaries.push(newSummary);
            return;
        }

        const newQuantity = parseInt(quantityMatch[1]);
        const newItemName = quantityMatch[2].trim();

        // Look for existing item with same name and modifications
        const existingIndex = summaries.findIndex(summary => {
            const existingLastCommaIndex = summary.lastIndexOf(',');
            if (existingLastCommaIndex === -1) return false;

            const existingContentPart = summary.substring(0, existingLastCommaIndex).trim();
            const existingFirstCommaIndex = existingContentPart.indexOf(',');
            if (existingFirstCommaIndex === -1) return false;

            const existingQuantityAndName = existingContentPart.substring(0, existingFirstCommaIndex).trim();
            const existingModifications = existingContentPart.substring(existingFirstCommaIndex + 1).trim();

            // Extract item name from existing summary
            const existingQuantityMatch = existingQuantityAndName.match(/^(\d+)\s+(.+)$/);
            if (!existingQuantityMatch) return false;

            const existingItemName = existingQuantityMatch[2].trim();

            // Check if item name and modifications match exactly
            return existingItemName === newItemName && existingModifications === newModifications;
        });

        if (existingIndex !== -1) {
            // Merge with existing item
            const existingSummary = summaries[existingIndex];
            const existingLastCommaIndex = existingSummary.lastIndexOf(',');
            const existingContentPart = existingSummary.substring(0, existingLastCommaIndex).trim();
            const existingPricePart = existingSummary.substring(existingLastCommaIndex + 1).trim();
            const existingPrice = parseFloat(existingPricePart);

            const existingFirstCommaIndex = existingContentPart.indexOf(',');
            const existingQuantityAndName = existingContentPart.substring(0, existingFirstCommaIndex).trim();
            const existingQuantityMatch = existingQuantityAndName.match(/^(\d+)\s+(.+)$/);

            if (existingQuantityMatch && !isNaN(existingPrice)) {
                const existingQuantity = parseInt(existingQuantityMatch[1]);

                const mergedQuantity = existingQuantity + newQuantity;
                const mergedPrice = existingPrice + newPrice;

                // Update the existing summary with merged values
                summaries[existingIndex] = `${mergedQuantity} ${newItemName},${newModifications},${mergedPrice}`;
            }
        } else {
            // Add as new item
            summaries.push(newSummary);
        }
    }

    /**
     * Generates summaries from current basket items
     * @returns Array of summary strings
     */
    private generateBasketSummaries(): Array<string> {
        const summaries: Array<string> = [];
        this.orderService.$storage.basket.forEach((offer) => {
            const getOfferSummary = this.chatBasketService.getOfferSummary(offer);
            if (getOfferSummary?.fullText) {
                this.mergeItemSummary(summaries, getOfferSummary.fullText);
            }
        });
        return summaries;
    }

    /**
     * Returns summary of current basket items and sends them to server
     * Function executes only if there's a valid session (sessionId !== 'new')
     * This is typically called when session changes from 'new' to a valid ID
     */

    async getOfferSummary(): Promise<Array<any>> {
        const summaries = this.generateBasketSummaries();

        console.log('Offer summaries:', summaries);

        // Create current basket summary string
        let currentBasketSummary = summaries.toString() || '';

        let tabotLastBasketSummary = this.orderService.$storage.tabotLastBasketSummary || '';

        // Check if basket changed (not just if it has items)
        //const basketChanged = currentBasketSummary !== tabotLastBasketSummary;
        const basketChanged = currentBasketSummary !== tabotLastBasketSummary; // || this.orderService.$storage.basket?.length !== 0;

        if (basketChanged) {
            // Update the last known state
            this.orderService.$storage.tabotLastBasketSummary = currentBasketSummary;

            // Check if there's a valid session - updateUserChanges cannot work with sessionId === 'new'
            if (this.chatService.getSessionId() !== 'new') {
                if (!currentBasketSummary) {
                    currentBasketSummary = 'new';
                }
                await this.updateUserChangesWithStream(currentBasketSummary);
            } else {
                console.log('Session not initialized, skipping updateUserChanges');
            }
        } else {
            console.log('Basket unchanged, skipping updateUserChanges');
        }

        return summaries;
    }

    /**
     * Updates user changes with streaming response and displays in chat
     */
    private async updateUserChangesWithStream(updateMessage: string): Promise<void> {
        try {
            this.loading.set(true);

            // Add assistant message indicating order checking is starting
            this.chatService.addMessage({
                role: 'assistant-temporary',
                content: this.appService.translate('tabot.checking_order'),
                direction: this.appService.direction,
                type: 'processing'
            });
            this.ensureScrollToBottom();

            // Add assistant message placeholder
            const currentMsgs: ChatMessage[] = [...this.messages(), { role: 'assistant', content: '', direction: this.appService.direction }];
            this.chatService.replaceConversation(currentMsgs);
            const index = currentMsgs.length - 1;

            this.cancelController = new AbortController();
			const language = this.appService.selectedLocale.name;

            const stream = await this.chatService.updateUserChanges(
                this.dialogLanguage(),
                language,
                updateMessage,
                this.cancelController.signal
            );

            const reader = stream.getReader();
            const decoder = new TextDecoder('utf-8');
            let fullText = '';
            let lastStreamUpdate = 0;
            let isFirstChunk = true;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                fullText += chunk;

                // Remove temporary messages when first chunk arrives
                if (isFirstChunk && chunk.trim().length > 0) {
                    this.removeTemporaryMessages();
                    isFirstChunk = false;
                }

                // Throttle updates to prevent too many DOM updates
                const now = Date.now();
                if (now - lastStreamUpdate > TYPING_EFFECT_CONFIG.updateThreshold) {
                    const updated = [...this.messages()];
                    // Filter streaming content to hide incomplete @{...}@ patterns
                    const filteredContent = this.chatService.filterStreamingContent(fullText);
                    updated[index] = {
                        role: 'assistant',
                        content: filteredContent,
                        direction: this.appService.direction,
						provisional: true
                    };

                    this.chatService.replaceConversation(updated);
                    this.scrollToBottom();
                    lastStreamUpdate = now;
                }
            }

            // Ensure final content is displayed
            if (fullText) {
                const updated = [...this.messages()];
                // Use the full text for final processing (processMessage will handle @{...}@ patterns)
                updated[index] = {
                    role: 'assistant',
                    content: fullText,
                    direction: this.appService.direction
                };
                this.chatService.replaceConversation(updated);
            }

        } catch (error) {
            // Don't log errors if the request was intentionally aborted during component destruction
            if (error instanceof DOMException && error.name === 'AbortError') {
                console.debug('User changes update was cancelled:', error.message);
                return; // Exit early, don't add error message to chat
            }

            console.error('Error updating user changes:', error);
            this.chatService.addMessage({
                role: 'assistant',
                content: this.appService.translate('tabot.general_error'),
                type: 'error',
                direction: this.appService.direction
            });
        } finally {
            this.loading.set(false);
            this.cancelController = null;
            this.scrollToBottom(true);
        }
    }

    /**
     * Handles successful transcription from the audio-recorder component
     */
    handleTranscriptionComplete(transcribedText: string): void {
        if (transcribedText && transcribedText.trim()) {
            // Add the transcribed text to the chat input
            this.prompt.set(transcribedText.trim());
            this.adjustTextareaHeight();

            // Optionally auto-send the transcribed message
            // this.sendMessage();
        }
    }

    /**
     * Handles transcription errors from the audio-recorder component
     */
    handleTranscriptionError(errorMessage: string): void {
        console.error('Transcription error:', errorMessage);

        // Add error message to chat for the user
        this.chatService.addMessage({
            role: 'assistant',
            content: this.appService.translate('tabot.voice_message_error'),
            type: 'error'
        });
    }

    // Language switching methods

	toggleTranslation() {
		this.appService.toggleTranslation();
		this.dialogLanguage.set(this.appService.locale);
		this.focusChatInput();
	}

    setDialogLanguage() {
        this.dialogLanguage.set(this.getCurrentLanguageName());
    }

    getCurrentLanguageName(): string {
        return this.appService.selectedLocale.name;
    }

    // Text size methods
    getStoredTextSize(): number {
        try {
            const stored = Number(localStorage.getItem('chat-text-size'));
            return (stored && !isNaN(stored) && stored <= 2) ? stored : 1
        } catch (error) {
			return 1;
        }
    }
    private saveTextSize(level: number): void {
        try {
            localStorage.setItem('chat-text-size', level.toString());
        } catch (error) {
            console.warn('Error saving text size to localStorage:', error);
        }
    }
    setTextSize(level: number): void {
        this.currentTextSize.set(level);
        this.saveTextSize(level);
    }


	protected readonly floor = floor;
	protected readonly Math = Math;
	protected readonly size = size;
}
